import { createHttpService } from "../ApiService";
import {
  TBranding,
  TUpdateBrandingRequest,
  TUploadLogoRequest,
  TUploadAssetRequest,
  TBrandingAsset,
} from "./branding.interfaces";
import {
  brandingListMock,
  brandingMock,
} from "../../__mocks__/branding/brandingMock";

export const useBrandingService = () => {
  const httpAPI = createHttpService();

  const shouldUseMock = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

  const getBranding = (instituteId?: string) => {
    if (shouldUseMock) {
      if (instituteId) {
        const branding = brandingListMock.find(
          (b) => b.instituteId === instituteId,
        );
        return Promise.resolve(branding || brandingMock);
      }
      return Promise.resolve(brandingMock);
    }
    const path = instituteId ? `?instituteId=${instituteId}` : "";
    return httpAPI.branding.get<TBranding>(path);
  };

  const updateBranding = (id: string, request: TUpdateBrandingRequest) => {
    if (shouldUseMock) {
      const index = brandingListMock.findIndex((b) => b.id === id);
      if (index === -1) {
        return Promise.reject(new Error("Branding not found"));
      }
      const updated: TBranding = {
        ...brandingListMock[index],
        ...request,
        updatedAt: new Date().toISOString(),
      };
      brandingListMock[index] = updated;
      return Promise.resolve(updated);
    }
    // Strip undefined values so JSON.stringify never produces an empty body {}
    const body = Object.fromEntries(
      Object.entries(request).filter(([, v]) => v !== undefined),
    ) as TUpdateBrandingRequest;
    return httpAPI.branding.patch<TBranding>(`/${id}`, body);
  };

  const uploadLogo = (
    request: TUploadLogoRequest,
    onProgress?: (progress: number) => void,
  ) => {
    if (shouldUseMock) {
      return new Promise<TBranding>((resolve) => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 25;
          if (onProgress) onProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            const logoUrl = URL.createObjectURL(request.file);
            const logoFileName = request.file.name;

            let branding = brandingListMock.find(
              (b) => b.instituteId === request.instituteId,
            );
            if (branding) {
              if (branding.logoUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(branding.logoUrl);
              }
              branding.logoUrl = logoUrl;
              branding.logoFileName = logoFileName;
              branding.updatedAt = new Date().toISOString();
            } else {
              branding = {
                id: `branding-${Date.now()}`,
                instituteId: request.instituteId,
                logoUrl,
                logoFileName,
                status: "active",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              brandingListMock.push(branding);
            }
            resolve(branding);
          }
        }, 200);
      });
    }

    const formData = new FormData();
    formData.append("file", request.file);
    formData.append("instituteId", request.instituteId);

    return httpAPI.branding.post<TBranding>("/logo", formData, {
      headers: { "Content-Type": "multipart/form-data" } as any,
      onUploadProgress: (progressEvent: { loaded: number; total?: number }) => {
        if (progressEvent.total && onProgress) {
          onProgress(
            Math.round((progressEvent.loaded * 100) / progressEvent.total),
          );
        }
      },
    } as any);
  };

  const uploadAsset = (
    request: TUploadAssetRequest,
    onProgress?: (progress: number) => void,
  ) => {
    if (shouldUseMock) {
      return new Promise<TBranding>((resolve) => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 25;
          if (onProgress) onProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
            const assetUrl = URL.createObjectURL(request.file);
            const newAsset: TBrandingAsset = {
              id: `asset-${Date.now()}`,
              type: request.assetType,
              fileName: request.file.name,
              fileUrl: assetUrl,
              fileSize: request.file.size,
              mimeType: request.file.type,
              uploadedAt: new Date().toISOString(),
            };

            let branding = brandingListMock.find(
              (b) => b.instituteId === request.instituteId,
            );
            if (branding) {
              branding.assets = [
                ...(branding.assets ?? []).filter(
                  (a) => a.type !== request.assetType,
                ),
                newAsset,
              ];
              branding.updatedAt = new Date().toISOString();
            } else {
              branding = {
                id: `branding-${Date.now()}`,
                instituteId: request.instituteId,
                assets: [newAsset],
                status: "active",
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              brandingListMock.push(branding);
            }
            resolve(branding);
          }
        }, 200);
      });
    }

    const formData = new FormData();
    formData.append("file", request.file);
    formData.append("instituteId", request.instituteId);
    formData.append("assetType", request.assetType);

    return httpAPI.branding.post<TBranding>("/assets", formData, {
      headers: { "Content-Type": "multipart/form-data" } as any,
      onUploadProgress: (progressEvent: { loaded: number; total?: number }) => {
        if (progressEvent.total && onProgress) {
          onProgress(
            Math.round((progressEvent.loaded * 100) / progressEvent.total),
          );
        }
      },
    } as any);
  };

  const deleteAsset = (instituteId: string, assetId: string) => {
    if (shouldUseMock) {
      const branding = brandingListMock.find(
        (b) => b.instituteId === instituteId,
      );
      if (!branding?.assets) return Promise.reject(new Error("Asset not found"));

      const idx = branding.assets.findIndex((a) => a.id === assetId);
      if (idx === -1) return Promise.reject(new Error("Asset not found"));

      const asset = branding.assets[idx];
      if (asset.fileUrl?.startsWith("blob:")) URL.revokeObjectURL(asset.fileUrl);

      branding.assets.splice(idx, 1);
      branding.updatedAt = new Date().toISOString();
      return Promise.resolve(branding);
    }

    return httpAPI.branding.delete<TBranding>(`/assets/${assetId}`, {
      params: { instituteId },
    });
  };

  return {
    getBranding,
    updateBranding,
    uploadLogo,
    uploadAsset,
    deleteAsset,
  };
};
